/**
 * Die senkrechte Ausdehnung eines Balkens (HM-104).
 *
 * Ein Balken misst seine Länge ab der Grundlinie; wie lang er in Bildpunkten
 * wird, sagt die Skala. Zwei Dinge daran waren falsch und sind hier
 * festgehalten, weil man sie in der Zeichenschicht nicht sieht:
 *
 * **Der Rand fraß den Balken.** Jeder Balken trug einen Rand in Grundfarbe an
 * allen vier Kanten — gedacht als Fuge zwischen zwei Segmenten eines Stapels.
 * Oben wie unten schrumpfte das Rechteck um den Rand, und der Strich malte
 * dann noch einen Bildpunkt nach innen: Vier Bildpunkte Höhe gingen an jedem
 * Balken verloren, egal wie klein er war. Bei 3 kW Spitze verschwand damit
 * alles unter rund 75 W — die Grundlast eines Haushalts — und die Grundlinie
 * des Diagramms zeigte eine Reihe leerer Stellen, die aussahen wie Lücken. Die
 * Fuge liegt seither nur dort, wo sie etwas trennt: an der Kante zwischen zwei
 * Segmenten. An der Grundlinie und am oberen Ende trennt sie nichts.
 *
 * **Ein Wert ungleich null bekommt mindestens einen Bildpunkt.** Eine Haarlinie
 * sagt „hier war etwas"; eine leere Stelle sagt „Lücke" oder „null", und
 * beides wäre falsch (Regel 8). Eine echte Null bleibt leer. Die Proportion zum
 * Spitzenwert bleibt davon unberührt, die Achse linear — eine logarithmische
 * oder gebrochene Achse machte aus dem Zehnfachen optisch das Doppelte und
 * sähe aus wie eine Messung (Regel 9).
 */

export interface BarExtent {
  /** Die obere Kante in Bildpunkten. */
  readonly top: number;
  /** Die Höhe in Bildpunkten, nie negativ. */
  readonly height: number;
  /**
   * Die Kante, die vom Nullpunkt wegzeigt — dort liegt die Fuge zu einem
   * Segment darüber.
   */
  readonly edge: number;
}

/**
 * @param level Die Bildschirmposition des Werts.
 * @param baseline Die Bildschirmposition der Null.
 * @param minimum Die kleinste sichtbare Höhe für einen Wert ungleich null.
 */
export function barExtent(level: number, baseline: number, minimum: number): BarExtent {
  if (level === baseline) return { top: baseline, height: 0, edge: baseline };

  // Auf dem Schirm wächst y nach unten: Ein positiver Wert liegt *über* der
  // Grundlinie, also bei kleinerem y.
  const upward = level < baseline;
  const height = Math.max(Math.abs(level - baseline), minimum);
  const edge = upward ? baseline - height : baseline + height;

  return { top: Math.min(edge, baseline), height, edge };
}
